// Pure presentation logic for the Beta Requests granted table (ra-4e38).
//
// Deliberately free of any Firebase import: the staleness rule, the sort order
// and the merge are what an operator's revoke decision rests on, so they are
// unit-testable — and renderable in the screenshot harness — without a Firebase
// app. The I/O half (the admin-gated `getBetaTesterInsights` callable) lives in
// betaStore.js; see functions/beta_tester_insights.js for why the data cannot
// be read from the browser at all.

/** Default cull threshold: no activity of any kind in this many days. */
export const STALE_DAYS = 60;

/** Sortable columns on the granted table. */
export const GRANTED_SORT_KEYS = ['lastActive', 'firstSeen', 'races', 'profile', 'email'];

/** Whole days between an ISO stamp and `now`, or null when it is absent. */
export function daysSince(iso, now = Date.now()) {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return null;
  return Math.floor((now - ms) / 86400000);
}

/**
 * Attach each row's insight (or null) so the table can render one shape.
 * A row with no insight keeps every engagement cell at "unknown" — which is
 * NOT the same as "inactive", and the stale filter treats it accordingly.
 */
export function mergeInsights(rows, insightsByUid) {
  const map = insightsByUid instanceof Map
    ? insightsByUid
    : new Map(Object.entries(insightsByUid || {}));
  return rows.map((r) => ({ ...r, insight: map.get(r.user_id) || null }));
}

/**
 * Did every server-side signal behind this insight actually resolve?
 *
 * The callable names failed collectors in `signalErrors` and nulls the values
 * they would have produced, rather than reporting them as zero. An incomplete
 * row can only ever understate activity and completeness, so it can only ever
 * push a live tester TOWARDS the cull list — never away from it.
 */
export function isComplete(insight) {
  return Boolean(insight) && ((insight.signalErrors || []).length === 0);
}

/**
 * Is this grant a cull candidate?
 *
 * Two independent reasons, both from Chris' brief: nobody has touched the app
 * in `staleDays`, or the profile was never filled in at all (a grant handed to
 * someone who never got started).
 *
 * Both require a COMPLETE insight. A row whose insight failed to load, or that
 * loaded with a failed signal inside it, is unknown — and unknown must never be
 * presented as safe to revoke. That is the whole reason the callable returns
 * nulls plus `signalErrors` instead of falling back to zeroes: a missing index
 * would otherwise render as "0 races, 0/4 profile" and manufacture a cull
 * candidate indistinguishable from a real one.
 */
export function isStaleGrant(row, { now = Date.now(), staleDays = STALE_DAYS } = {}) {
  const insight = row && row.insight;
  if (!isComplete(insight)) return false;
  // A grant pointing at an auth account that no longer exists is dead by
  // definition — and it is the one dead grant that can still look alive, since
  // a device row left behind by the deleted account keeps ticking the activity
  // clock. Without this it hides from the very filter meant to surface it.
  if (insight.authExists === false) return true;
  if (insight.profile && insight.profile.setCount === 0) return true;
  const days = daysSince(insight.lastActiveAt, now);
  if (days === null) return true; // complete insight, but no activity clock ever ticked
  return days >= staleDays;
}

function sortValue(row, key) {
  const insight = row.insight;
  switch (key) {
    case 'lastActive': {
      const ms = insight && insight.lastActiveAt ? Date.parse(insight.lastActiveAt) : NaN;
      return Number.isNaN(ms) ? null : ms;
    }
    case 'firstSeen':
      return row.earliest ? row.earliest.getTime() : null;
    case 'races':
      // `?? null` and not `|| null`: a real 0 races must sort as zero, while a
      // failed read (null) sinks to the bottom with the other unknowns.
      return insight ? (insight.raceCount ?? null) : null;
    case 'profile':
      return insight && insight.profile ? insight.profile.setCount : null;
    case 'email':
      // The SAME fallback the cell renders. Sorting on row.email alone sends a
      // grant that only has an auth email to the bottom as "unknown", under a
      // heading that is showing its address.
      return ((row.email || (insight && insight.email) || '').toLowerCase()) || null;
    default:
      return null;
  }
}

/**
 * Sort granted rows by one column.
 *
 * Rows with no value for the sort key always sink to the BOTTOM, in both
 * directions. An unknown last-active is not "the oldest" — sorting it to the
 * top of an ascending (stalest-first) list would put "we don't know" where the
 * operator expects "definitely dead", and that is the click that revokes the
 * wrong grant.
 */
export function sortGranted(rows, key = 'lastActive', direction = 'asc') {
  const dir = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null && bv === null) return (a.user_id || '').localeCompare(b.user_id || '');
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return (a.user_id || '').localeCompare(b.user_id || '');
  });
}

/** "3 mo", "12 d", "today" — compact enough for a table cell. */
export function formatAge(iso, now = Date.now()) {
  const days = daysSince(iso, now);
  if (days === null) return '—';
  if (days <= 0) return 'today';
  if (days === 1) return '1 d';
  if (days < 60) return `${days} d`;
  return `${Math.floor(days / 30)} mo`;
}

/** The gaps in a profile, e.g. "club, polars" — or '' when it is complete. */
export function missingProfileFields(insight) {
  const fields = insight && insight.profile && insight.profile.fields;
  if (!fields) return '';
  return Object.keys(fields).filter((k) => !fields[k]).join(', ');
}
